import React from "react";
import { Users, Calendar, Tag, Gift, Sparkles, BookOpen, Image as ImageIcon, FileText, CreditCard, Palette, Receipt } from "lucide-react";
import {
  Text, Money, DateText, ExpiryText, Badge, TenantCell, LongText, Ref, MediaPreview, MediaName,
  ColorSwatches, Logo, formatBytes, DASH,
} from "./globalDataCells";

/**
 * The twelve Global Data tabs as data (docs/auth-and-admin/global-data-contract.md section 4).
 *
 * One tab = one config object: its route, its icon, its default sort, the statuses its
 * filter offers and its columns. A column is `{ id, headerKey, sort?, render(row, ctx) }`;
 * `sort` is the wire key the server sorts on, and a column without it has no sort button.
 * `ctx` is `{ t, lang, loc }`. There is no per-tab JSX in the page itself.
 */

/**
 * Every closed vocabulary the page labels, in ONE place. A value here needs one i18n key
 * per language (`gdv.status.<group>.<value>`, or `gdv.<group>.<value>` for the two
 * attribute groups); a value the server adds later still renders, as its raw code.
 */
export const VALUE_GROUPS = {
  customers: ["lead", "active", "past"],
  bookings: ["pending", "pending_payment", "confirmed", "paid_needs_attention", "cancelled", "completed", "no_show"],
  programs: ["active", "inactive"],
  promos: ["active", "inactive", "expired"],
  giftcards: ["active", "partially_redeemed", "redeemed", "cancelled", "refunded"],
  media: ["image", "video", "document", "file"],
  blog: ["published", "draft"],
  billing: ["initiated", "completed", "failed", "expired", "pending", "refunded", "refund_unverified"],
  invoices: ["draft", "sent", "paid", "cancelled", "refunded", "overdue_notified"],
  insights: ["ai", "fallback"],
  source: ["admin", "sold"],
  visibility: ["public", "private"],
};

const ATTRIBUTE_GROUPS = new Set(["source", "visibility"]);

/** The i18n key of one value of one group. */
export const valueKey = (group, value) =>
  ATTRIBUTE_GROUPS.has(group) ? `gdv.${group}.${value}` : `gdv.status.${group}.${value}`;

const TONES = {
  active: "good", confirmed: "good", paid: "good", completed: "good", published: "good", redeemed: "good",
  failed: "bad", cancelled: "bad", expired: "bad", no_show: "bad", refunded: "bad",
  ai: "good", fallback: "warn",
  overdue_notified: "warn", paid_needs_attention: "warn", refund_unverified: "warn", pending: "warn", pending_payment: "warn", partially_redeemed: "warn",
};

/**
 * The practitioner's invoice number (`FAC-YYYY-NNNN`), assigned at the first send. An
 * unnumbered draft says so and an invoice cancelled before it was ever sent has nothing to
 * show: the row id is NEVER a stand-in. Once paid, the tax receipt number is a secondary line.
 */
function InvoiceNumber({ row, t }) {
  const label = row.number
    ? <Ref value={row.number} />
    : <span className="text-slate-400">{row.status === "draft" ? t("gdv.invoiceNumber.draft") : DASH}</span>;
  if (!row.receipt_number) return label;
  return (
    <>
      {label}
      <div className="text-xs text-slate-400">{t("gdv.invoiceNumber.receipt", { number: row.receipt_number })}</div>
    </>
  );
}

const status = (group, value, t) => <Badge value={value} labelKey={valueKey(group, value)} t={t} tone={TONES[value]} />;

/**
 * `width` is the column's basis in rem. The table is `table-layout: fixed` (see
 * GlobalDataView): the widths are what the browser lays out, the page's width is spread
 * across them in proportion, and each tab's widths sum to at most `MAX_TAB_WIDTH_REM` so a
 * tab fits its container with every column visible at the product's 1440px desktop width.
 * Text longer than its column wraps or truncates INSIDE the cell; it never widens the table.
 */
/**
 * The basis of a money column, in rem. `Money` keeps the number whole and wraps only between
 * the number and its currency code, so the column has to hold the number: a nine-digit
 * amount ("123456789.00") is about 92px at the page's text size, plus the cell's 24px of
 * padding, which is 7.25rem. 8rem leaves room for a ten-digit one.
 */
export const MONEY_COL_REM = 8;

const col = (id, headerKey, width, render, sort) => ({ id, headerKey, width, render, ...(sort ? { sort } : {}) });
const tenantCol = (width) => col("tenant", "gdv.h.tenant", width, (r) => <TenantCell tenant={r.tenant} />);
const createdCol = (width) => col("created", "gdv.h.created", width, (r, { lang }) => <DateText value={r.created_at} lang={lang} />, "created_at");

/** The widest a tab's columns may sum to: the table area of a 1440px viewport is 68rem. */
export const MAX_TAB_WIDTH_REM = 64;

/** The sum of a tab's column bases, in rem: the narrowest the table is laid out. */
export const tableWidthRem = (tab) => tab.columns.reduce((sum, c) => sum + c.width, 0);

export const TABS = [
  {
    key: "customers", labelKey: "gdv.tab.customers", searchKey: "gdv.search.customers", icon: Users, defaultSort: "-created_at", statuses: VALUE_GROUPS.customers,
    columns: [
      col("name", "gdv.h.name", 10, (r) => <Text value={r.display_name} className="font-medium" />, "display_name"),
      col("email", "gdv.h.email", 16, (r) => (
        present(r.email)
          ? <span className="text-slate-500">{r.email}{r.extra_emails > 0 && <span className="ml-1 text-xs text-slate-400">+{r.extra_emails}</span>}</span>
          : <Text value={null} />
      )),
      col("phone", "gdv.h.phone", 9, (r) => <Text value={r.phone} className="text-slate-500" />),
      col("stage", "gdv.h.status", 6.5, (r, { t }) => status("customers", r.stage, t)),
      tenantCol(10),
      { ...createdCol(7.5), headerKey: "gdv.h.registered" },
    ],
  },
  {
    key: "bookings", labelKey: "gdv.tab.bookings", searchKey: "gdv.search.bookings", icon: Calendar, defaultSort: "-created_at", statuses: VALUE_GROUPS.bookings,
    columns: [
      col("patient", "gdv.h.patient", 9, (r) => <Text value={r.patient} className="font-medium" />),
      col("email", "gdv.h.email", 14, (r) => <Text value={r.email} className="text-slate-500" />),
      col("service", "gdv.h.service", 12, (r) => <Text value={r.service} className="text-slate-500" />),
      col("status", "gdv.h.status", 8, (r, { t }) => status("bookings", r.status, t)),
      tenantCol(9),
      col("slot", "gdv.h.date", 8.5, (r, { lang }) => <DateText value={r.slot_start} lang={lang} withTime />, "slot_start"),
    ],
  },
  {
    key: "programs", labelKey: "gdv.tab.programs", searchKey: "gdv.search.programs", icon: BookOpen, defaultSort: "-created_at", statuses: VALUE_GROUPS.programs,
    columns: [
      col("name", "gdv.h.title", 18, (r, { loc, t }) => (
        <span className="font-medium">
          {loc(r, "name") || DASH}
          {r.featured && <span className="ml-2 text-xs rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">{t("gdv.featured")}</span>}
        </span>
      )),
      col("duration", "gdv.h.duration", 9, (r) => <Text value={r.duration_label} className="text-slate-500" />),
      col("price", "gdv.h.price", MONEY_COL_REM, (r) => <Money value={r.price} />, "price_cents"),
      tenantCol(10),
      col("status", "gdv.h.status", 6, (r, { t }) => status("programs", r.active ? "active" : "inactive", t)),
    ],
  },
  {
    key: "promos", labelKey: "gdv.tab.promos", searchKey: "gdv.search.promos", icon: Tag, defaultSort: "-created_at", statuses: VALUE_GROUPS.promos,
    columns: [
      col("code", "gdv.h.code", 14, (r) => <code className="font-medium text-amber-700">{r.code}</code>),
      col("discount", "gdv.h.discount", MONEY_COL_REM, (r) => (
        r.percent_bp !== null && r.percent_bp !== undefined
          ? <span>{`${r.percent_bp / 100}%`}</span>
          : <Money value={r.amount} />
      )),
      col("uses", "gdv.h.uses", 7, (r) => <span className="text-slate-500">{`${r.used_count || 0} / ${r.max_uses ? r.max_uses : "∞"}`}</span>, "used_count"),
      col("expires", "gdv.h.expires", 7.5, (r, { lang, t }) => (
        <ExpiryText value={r.expires_at} unreadable={r.expiry_unreadable === true} unreadableLabel={t("gdv.expiryUnreadable")} lang={lang} />
      ), "expires_at"),
      tenantCol(10),
      col("state", "gdv.h.status", 7, (r, { t }) => status("promos", r.state, t)),
    ],
  },
  {
    key: "giftcards", labelKey: "gdv.tab.giftcards", searchKey: "gdv.search.giftcards", icon: Gift, defaultSort: "-created_at", statuses: VALUE_GROUPS.giftcards,
    columns: [
      col("code", "gdv.h.code", 11, (r) => <code className="font-medium text-amber-700">{r.code}</code>),
      col("initial", "gdv.h.initial", MONEY_COL_REM, (r) => <Money value={r.initial} />),
      col("balance", "gdv.h.balance", MONEY_COL_REM, (r) => <Money value={r.balance} />, "balance_cents"),
      col("status", "gdv.h.status", 8, (r, { t }) => status("giftcards", r.status, t)),
      col("source", "gdv.h.source", 5.5, (r, { t }) => <Badge value={r.source} labelKey={valueKey("source", r.source)} t={t} />),
      tenantCol(8.5),
      col("recipient", "gdv.h.recipient", 13, (r) => (
        present(r.recipient_email) || present(r.recipient_name)
          ? <span className="text-xs text-slate-500">{[r.recipient_name, r.recipient_email].filter(present).join(" · ")}</span>
          : <Text value={null} />
      )),
    ],
  },
  {
    key: "media", labelKey: "gdv.tab.media", searchKey: "gdv.search.media", icon: ImageIcon, defaultSort: "-created_at", statuses: VALUE_GROUPS.media,
    columns: [
      col("preview", "gdv.h.preview", 4.5, (r) => <MediaPreview row={r} />),
      col("file", "gdv.h.file", 15, (r, { t }) => <MediaName row={r} t={t} />),
      col("kind", "gdv.h.type", 6, (r, { t }) => status("media", r.kind, t)),
      col("size", "gdv.h.size", 5.5, (r) => <Text value={formatBytes(r.size)} className="text-xs text-slate-500" />, "size"),
      tenantCol(10),
      createdCol(7.5),
    ],
  },
  {
    key: "content", labelKey: "gdv.tab.content", searchKey: "gdv.search.content", icon: FileText, defaultSort: "-updated_at", statuses: null,
    columns: [
      col("key", "gdv.h.key", 12, (r) => <code className="text-xs text-amber-700">{r.key}</code>, "key"),
      col("valueFr", "gdv.h.textFr", 16, (r, { t }) => <LongText value={r.value_fr} t={t} />),
      col("valueEn", "gdv.h.textEn", 16, (r, { t }) => <LongText value={r.value_en} t={t} />),
      tenantCol(9),
      col("updated", "gdv.h.updated", 7.5, (r, { lang }) => <DateText value={r.updated_at} lang={lang} />, "updated_at"),
    ],
  },
  {
    key: "blog", labelKey: "gdv.tab.blog", searchKey: "gdv.search.blog", icon: FileText, defaultSort: "-created_at", statuses: VALUE_GROUPS.blog,
    columns: [
      col("title", "gdv.h.title", 17, (r, { loc }) => <Text value={loc(r, "title") || r.slug} className="font-medium" />),
      col("tag", "gdv.h.tag", 8, (r, { loc }) => <Text value={loc(r, "tag")} className="text-xs text-slate-500" />),
      col("status", "gdv.h.status", 6.5, (r, { t }) => status("blog", r.published ? "published" : "draft", t)),
      col("likes", "gdv.h.likes", 5, (r) => <span className="tabular-nums text-slate-500">{r.likes_count ?? 0}</span>, "likes_count"),
      tenantCol(10),
      createdCol(7.5),
    ],
  },
  {
    key: "billing", labelKey: "gdv.tab.billing", searchKey: "gdv.search.billing", icon: CreditCard, defaultSort: "-created_at", statuses: VALUE_GROUPS.billing,
    columns: [
      col("reference", "gdv.h.id", 12, (r) => <Ref value={r.reference} />),
      col("amount", "gdv.h.amount", MONEY_COL_REM, (r) => <Money value={r.amount} />, "amount"),
      col("plan", "gdv.h.plan", 10, (r) => <Text value={r.plan_name || r.plan_id} className="text-slate-500" />),
      col("status", "gdv.h.status", 7, (r, { t }) => status("billing", r.status, t)),
      col("provider", "gdv.h.provider", 6, (r) => <Text value={r.provider} className="text-xs text-slate-500" />),
      tenantCol(10),
      createdCol(7.5),
    ],
  },
  {
    key: "invoices", labelKey: "gdv.tab.invoices", searchKey: "gdv.search.invoices", icon: Receipt, defaultSort: "-created_at", statuses: VALUE_GROUPS.invoices,
    columns: [
      col("number", "gdv.h.number", 12, (r, { t }) => <InvoiceNumber row={r} t={t} />),
      col("client", "gdv.h.client", 13, (r) => <Text value={r.client_name || r.client_email} className="text-slate-500" />),
      col("amount", "gdv.h.amount", MONEY_COL_REM, (r) => <Money value={r.amount} />, "amount_cents"),
      col("status", "gdv.h.status", 7, (r, { t }) => status("invoices", r.status, t)),
      tenantCol(10),
      col("paid", "gdv.h.paidOn", 7.5, (r, { lang }) => <DateText value={r.paid_at} lang={lang} />, "paid_at"),
    ],
  },
  {
    key: "branding", labelKey: "gdv.tab.branding", searchKey: "gdv.search.branding", icon: Palette, defaultSort: "-created_at", statuses: null,
    columns: [
      col("logo", "gdv.h.logo", 5.5, (r) => <Logo url={r.logo_url} />),
      { ...tenantCol(11), sort: "name" },
      col("palette", "gdv.h.palette", 6.5, (r) => <ColorSwatches row={r} />),
      col("font", "gdv.h.font", 7.5, (r) => <Text value={r.font_pair} className="text-xs text-slate-500" />),
      col("tagline", "gdv.h.tagline", 11, (r) => <Text value={r.tagline} className="text-xs text-slate-500" />),
      col("domain", "gdv.h.domain", 12, (r, { t }) => (
        present(r.custom_domain)
          ? (
            <span className="text-xs text-slate-500">
              {r.custom_domain}{" "}
              <span className="text-slate-400">({t(`gdv.domainStatus.${r.custom_domain_status || "pending_dns"}`)})</span>
            </span>
          )
          : <Text value={null} />
      )),
      createdCol(7),
    ],
  },
  {
    key: "insights", labelKey: "gdv.tab.insights", searchKey: "gdv.search.insights", icon: Sparkles, defaultSort: "-generated_at", statuses: VALUE_GROUPS.insights,
    columns: [
      tenantCol(11),
      col("date", "gdv.h.date", 7, (r) => <Text value={r.date} className="text-xs text-slate-500" />),
      col("source", "gdv.h.source", 7, (r, { t }) => (
        <>
          {status("insights", r.source, t)}
          {r.source === "fallback" && present(r.fallback_reason) && (
            <div data-testid="gdv-insight-reason" className="text-[11px] text-slate-400 mt-0.5">
              {t(`gdv.insights.reason.${r.fallback_reason}`, undefined, String(r.fallback_reason))}
            </div>
          )}
        </>
      )),
      col("summary", "gdv.h.briefing", 26, (r, { t }) => <LongText value={r.summary} t={t} />),
      col("generated", "gdv.h.generatedOn", 9, (r, { lang }) => <DateText value={r.generated_at} lang={lang} withTime />, "generated_at"),
    ],
  },
];

function present(v) {
  return v !== null && v !== undefined && v !== "";
}
